// Entry-point do viewer Lumos. Lê ?p=<slug>, carrega
// `projetos/<slug>/manifest.json` e cada disciplina como .frag.

import * as THREE from "three";
import * as OBC from "@thatopen/components";
import { LodMode } from "@thatopen/fragments";
import type { Manifest, ManifestDisciplina } from "./types";

const $ = <T extends Element = HTMLElement>(sel: string) => document.querySelector(sel) as T;
const $$ = (sel: string) => Array.from(document.querySelectorAll(sel));

const slug = new URLSearchParams(location.search).get("p");
if (!slug || !/^[a-z2-9]{8,24}$/.test(slug)) {
  showError("Link inválido ou expirado.");
  throw new Error("Slug ausente ou inválido");
}

const baseUrl = `/projetos/${slug}`;

(async () => {
  let manifest: Manifest;
  try {
    const r = await fetch(`${baseUrl}/manifest.json`, { cache: "no-cache" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    manifest = await r.json();
  } catch (e) {
    showError(`Não conseguimos carregar o projeto. (${(e as Error).message})`);
    return;
  }

  // Header
  $("#lumos-projeto .num").textContent = `PROJETO #${manifest.projeto.numero}`;
  $("#lumos-projeto .nome").textContent = manifest.projeto.nome;
  $("#lumos-expira").textContent = `Válido até ${formatDate(manifest.expiraEm)}`;
  document.title = `Lumos · ${manifest.projeto.numero} · ${manifest.projeto.nome}`;

  // ---- Setup viewer ----
  const components = new OBC.Components();

  const worlds = components.get(OBC.Worlds);
  const world = worlds.create<OBC.SimpleScene, OBC.OrthoPerspectiveCamera, OBC.SimpleRenderer>();
  world.scene = new OBC.SimpleScene(components);
  world.renderer = new OBC.SimpleRenderer(components, $("#lumos-canvas") as HTMLElement);
  world.camera = new OBC.OrthoPerspectiveCamera(components);

  components.init();
  world.scene.setup();
  world.scene.three.background = new THREE.Color(0xf3f4f6);

  // grid
  const grids = components.get(OBC.Grids);
  grids.create(world);

  // Câmera inicial. Antes de posicionar, esperar o container TER TAMANHO: se a página
  // abre num contexto de largura/altura zero (aba oculta, iframe recolhido, pane de
  // preview que ainda não foi exibido), o aspect da câmera vira 0/0 = NaN, o
  // camera-controls guarda NaN no estado interno e a partir daí NENHUM setLookAt ou
  // fitToBox recupera — o render continua emitindo draw calls e a tela fica branca
  // para sempre. Diagnosticado em 19/08/2026.
  await esperarTamanho($("#lumos-canvas") as HTMLElement);
  await world.camera.controls.setLookAt(50, 50, 50, 0, 0, 0);

  // fragments
  const fragments = components.get(OBC.FragmentsManager);
  await fragments.init(await OBC.FragmentsManager.getWorker());
  world.camera.controls.addEventListener("rest", () => fragments.core.update(true));

  const loadedModels = new Map<string, { disc: ManifestDisciplina; model: any; bbox: THREE.Box3 }>();
  const aabbAll = new THREE.Box3();

  // ---- Sidebar ----
  buildDisciplineList(manifest, async (d, on) => {
    if (on) {
      await loadDisc(d);
    } else {
      const entry = loadedModels.get(d.key);
      if (entry) {
        await fragments.core.disposeModel(entry.model.modelId);
        loadedModels.delete(d.key);
      }
    }
    updateBoxAndCamera();
  });

  const corte = criarCorte(world, () => aabbAll);

  // Alça de depuração: o viewer roda em produção sem devtools do lado do cliente,
  // e diagnosticar "tela branca" sem acesso ao estado custa um deploy por hipótese.
  (window as any).__lumos = { THREE, components, world, fragments, loadedModels, corte, manifest, aabb: () => aabbAll };
  window.addEventListener("error", (e) => console.error("[lumos] erro:", e.message, e.error));
  window.addEventListener("unhandledrejection", (e) => console.error("[lumos] promessa rejeitada:", e.reason));

  buildFloorList(manifest, async (storey) => {
    // Cortar no pavimento é o atalho que mais economiza clique: põe o plano
    // horizontal 1,60 m acima do piso escolhido, que é a altura de uma planta
    // baixa de verdade — vê parede, porta, ponto e tubulação daquele andar.
    if (corte.cortarNoPavimento() && isFinite(storey.elev)) {
      corte.cortarEmY(storey.elev + 1.6);
      return;
    }
    const matches: THREE.Box3[] = [];
    for (const [, entry] of loadedModels) {
      const floor = entry.disc.andares.find(s => s.name === storey.name);
      if (floor && entry.bbox) matches.push(entry.bbox);
    }
    if (matches.length) {
      const box = new THREE.Box3();
      matches.forEach(b => box.union(b));
      fitTo(box);
    }
  });

  // carrega disciplinas visíveis por padrão
  showLoader(`Carregando ${manifest.disciplinas.length} disciplinas…`, 0);
  let i = 0;
  for (const d of manifest.disciplinas.filter(x => x.visivelPorPadrao)) {
    setLoaderProgress(`Carregando ${d.label}…`, (i / manifest.disciplinas.length) * 100);
    await loadDisc(d);
    i++;
  }
  updateBoxAndCamera(true); // fit forçado uma vez quando termina o batch inicial
  corte.recalibrar();       // faixa dos sliders = extensão real do que carregou
  hideLoader();

  // ---- Tools ----
  $("[data-tool='fit']")?.addEventListener("click", () => updateBoxAndCamera(true));
  $("[data-tool='shot']")?.addEventListener("click", () => downloadScreenshot(manifest));

  // ---- Mobile menu ----
  $("#lumos-menu-toggle")?.addEventListener("click", () => $("#lumos-sidebar")?.classList.add("open"));
  $("#lumos-sidebar-close")?.addEventListener("click", () => $("#lumos-sidebar")?.classList.remove("open"));

  async function loadDisc(d: ManifestDisciplina) {
    // Os .frag têm nome fixo (HID.frag, ELE.frag...) e mudam de conteúdo a cada
    // republicação. Com `cache: "force-cache"` puro o navegador reusava a cópia
    // baixada na primeira visita PARA SEMPRE, sem revalidar — quem já tinha aberto
    // o link nunca via republicação nenhuma. Foi o que escondeu várias correções
    // seguidas no projeto 601 em 04/08/2026: o servidor entregava o arquivo novo e
    // o navegador continuava desenhando o antigo.
    // A versão vem do manifest (buscado com no-cache, então sempre atual) e entra
    // na URL. Conteúdo novo = URL nova = chave de cache nova, sem precisar de hard
    // reload. Entre builds a URL não muda e o cache agressivo segue valendo, que
    // importa porque os fragments têm alguns MB.
    const versao = encodeURIComponent(manifest.geradoEm ?? "0");
    const r = await fetch(`${baseUrl}/${d.arquivo}?v=${versao}`, { cache: "force-cache" });
    if (!r.ok) { console.warn(`Falha ${d.key}: HTTP ${r.status}`); return; }
    const buf = await r.arrayBuffer();
    const model = await fragments.core.load(buf, { modelId: d.key });
    world.scene.three.add(model.object);
    const { scale, translate, rotateY } = d.transform ?? { scale: 1, translate: [0, 0, 0] };
    if (scale !== 1) model.object.scale.setScalar(scale);
    // Ordem do three.js na matriz do objeto: mundo = posição + rotação · (escala · local).
    // A rotação é em torno da origem do mundo, então o translate abaixo já é calculado
    // no build considerando o modelo JÁ girado — os dois não são independentes.
    if (rotateY) model.object.rotation.y = (rotateY * Math.PI) / 180;
    if (translate.some(x => x !== 0)) model.object.position.set(translate[0], translate[1], translate[2]);
    model.object.updateMatrixWorld(true);
    // LodMode.DEFAULT (padrão) faz frustum culling + LOD por distância. Sem useCamera
    // explícito o culling fica errático e esconde geometria visível (peças cortadas).
    // Pacotes Lumos são pequenos (<10 MB) — ALL_VISIBLE é simples e correto.
    model.useCamera(world.camera.three);
    await model.setLodMode(LodMode.ALL_VISIBLE);
    await fragments.core.update(true);
    // model.box JÁ vem em coordenadas de MUNDO — o fragments o recalcula depois do
    // updateMatrixWorld acima, então ele reflete scale/rotateY/translate. Aplicar
    // matrixWorld de novo somava o transform DUAS vezes e jogava a caixa de
    // enquadramento para fora do modelo: no 609, o EST (translate 25,43/48,36) tinha
    // caixa medida em X 26,78..52,04 e o viewer registrava X 52,00..77,31, com a
    // geometria renderizada no lugar certo. O efeito era o "Enquadrar" mirar espaço
    // vazio e o prédio virar um ponto no canto — pior quanto maior o transform.
    // Conferido em 26/08/2026 contra Box3.setFromObject(model.object), que devolve a
    // caixa da geometria efetivamente desenhada: bate com model.box, não com o produto.
    const bbox = model.box.clone();
    loadedModels.set(d.key, { disc: d, model, bbox });
  }

  function updateBoxAndCamera(force = false) {
    aabbAll.makeEmpty();
    for (const { bbox } of loadedModels.values()) {
      if (!bbox.isEmpty() && isFinite(bbox.min.x)) aabbAll.union(bbox);
    }
    if (!aabbAll.isEmpty() && force) fitTo(aabbAll);
  }

  function fitTo(box: THREE.Box3) {
    const pad = box.getSize(new THREE.Vector3()).length() * 0.05;
    const padded = box.clone().expandByScalar(pad);
    world.camera.controls.fitToBox(padded, true, { paddingLeft: 0.1, paddingRight: 0.1, paddingTop: 0.1, paddingBottom: 0.1 });
  }

  // primeiro fit quando todas as visíveis carregaram
  if (!aabbAll.isEmpty()) fitTo(aabbAll);

  // Rede de segurança para a câmera NaN. A espera por tamanho antes do primeiro
  // setLookAt resolve o caso comum, mas se o container demorar mais que o limite
  // (aba de fundo que só aparece muito depois) o aspect ainda nasce 0/0 = NaN — e o
  // camera-controls, uma vez com NaN no estado interno, NÃO se recupera com
  // setLookAt nem com fitToBox: desenha e não aparece nada. Aqui a gente observa o
  // container e, na primeira vez que ele tiver tamanho de verdade com a câmera
  // inválida, zera os controles e reenquadra.
  const alvoObs = $("#lumos-canvas") as HTMLElement;
  const obs = new ResizeObserver(() => {
    if (alvoObs.clientWidth === 0 || alvoObs.clientHeight === 0) return;
    // Checar só a POSIÇÃO não basta: em 26/08/2026, no 609, a câmera apareceu com
    // position finita e quaternion NaN — a rede não disparava e a tela ficava branca.
    // O aspect 0/0 contamina a rotação, não a translação. Testar a matriz inteira
    // (que cobre posição E rotação) mais o aspect.
    const cam = world.camera.three;
    cam.updateMatrixWorld(true);
    const saudavel = cam.matrixWorld.elements.every(Number.isFinite) && Number.isFinite(cam.aspect);
    if (saudavel) return;
    // E zerar o quaternion na mão: medido no mesmo dia, com NaN na rotação nem
    // setLookAt nem fitToBox recuperam — o camera-controls parte do estado sujo.
    cam.quaternion.set(0, 0, 0, 1);
    cam.rotation.set(0, 0, 0);
    cam.up.set(0, 1, 0);
    if (!Number.isFinite(cam.aspect) && alvoObs.clientHeight > 0) {
      cam.aspect = alvoObs.clientWidth / alvoObs.clientHeight;
    }
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld(true);
    try { (world.camera.controls as any).reset?.(false); } catch { /* segue */ }
    world.camera.controls.setLookAt(50, 50, 50, 0, 0, 0, false);
    if (!aabbAll.isEmpty()) fitTo(aabbAll);
    world.renderer?.update?.();
  });
  obs.observe(alvoObs);
})();

// ---- UI helpers ----

/** Resolve quando o elemento tiver largura e altura > 0 (ou após o limite de espera). */
function esperarTamanho(el: HTMLElement, limiteMs = 4000) {
  return new Promise<void>((resolve) => {
    const t0 = performance.now();
    const checa = () => {
      if ((el.clientWidth > 0 && el.clientHeight > 0) || performance.now() - t0 > limiteMs) resolve();
      else requestAnimationFrame(checa);
    };
    checa();
  });
}

function buildDisciplineList(manifest: Manifest, onToggle: (d: ManifestDisciplina, on: boolean) => void) {
  const ul = $("#lumos-discs");
  ul.innerHTML = "";
  for (const d of manifest.disciplinas) {
    const li = document.createElement("li");
    li.dataset.key = d.key;
    li.innerHTML = `
      <div class="lumos-check ${d.visivelPorPadrao ? "on" : ""}">${d.visivelPorPadrao ? "✓" : ""}</div>
      <div class="lumos-swatch" style="background:${d.cor}"></div>
      <span>${d.label}</span>
      <span class="lumos-meta">${(d.tamanho_frag_bytes / 1024 / 1024).toFixed(1)} MB</span>
    `;
    let on = d.visivelPorPadrao;
    li.addEventListener("click", () => {
      on = !on;
      const check = li.querySelector(".lumos-check")!;
      check.classList.toggle("on", on);
      check.textContent = on ? "✓" : "";
      onToggle(d, on);
    });
    ul.appendChild(li);
  }
}

function buildFloorList(manifest: Manifest, onPick: (s: { name: string; elev: number }) => void) {
  const ul = $("#lumos-floors");
  ul.innerHTML = "";
  // A elevação do IfcBuildingStorey vem na unidade ORIGINAL de cada modelo — a
  // arquitetura deste projeto exporta em mm, o PCI em cm e o resto em m. Sem
  // normalizar, a lista mistura 22400, 2240 e 22,4 e a ordem sai embaralhada,
  // além de inviabilizar cortar por pavimento.
  const fator = (u?: string) => (u === "mm" ? 0.001 : u === "cm" ? 0.01 : 1);
  const nome = (s: string) => s.replace(/\s+/g, " ").trim();
  const emMetros = (d: ManifestDisciplina) =>
    d.andares.filter(s => s.elev != null).map(s => ({ name: nome(s.name), elev: s.elev! * fator(d.unidadeOriginal) }));

  // Quem define quais pavimentos EXISTEM é a arquitetura — é o projeto legal e o
  // único jogo completo. As demais disciplinas repetem andar (o PCI cria um
  // IfcBuildingStorey por cota de forro) e inventam nível de trabalho
  // ("MODULO LAJE CORPORATIVA" a −10,20 m), que não são pavimento nenhum.
  const arq = manifest.disciplinas.find(d => d.key === "ARQ");
  let all: { name: string; elev: number }[];
  if (arq && emMetros(arq).length >= 2) {
    all = emMetros(arq);
  } else {
    // Sem arquitetura: une tudo agrupando por cota (15 cm) e fica com o nome mais
    // curto, que costuma ser o do pavimento e não o da prancha.
    const porCota = new Map<number, { name: string; elev: number }>();
    for (const d of manifest.disciplinas) {
      for (const s of emMetros(d)) {
        const chave = Math.round(s.elev / 0.15);
        const atual = porCota.get(chave);
        if (!atual || s.name.length < atual.name.length) porCota.set(chave, s);
      }
    }
    all = [...porCota.values()];
  }
  all = all.sort((a, b) => a.elev - b.elev);
  for (const s of all) {
    const li = document.createElement("li");
    li.innerHTML = `<span>${s.name}</span><span class="lumos-meta">${s.elev.toFixed(2).replace(".", ",")} m</span>`;
    li.style.display = "flex";
    li.style.justifyContent = "space-between";
    li.style.gap = "8px";
    li.addEventListener("click", () => {
      $$("#lumos-floors li").forEach(x => x.classList.remove("active"));
      li.classList.add("active");
      onPick(s);
    });
    ul.appendChild(li);
  }
}

// ---- Corte (planos de seção) ----
//
// Recurso pedido pelo Thiago em 19/08/2026, no espírito do BIMcollab / BIM Vision:
// escolher o plano e arrastar, para ver o miolo do prédio sem esconder disciplina.
//
// Implementação por `renderer.clippingPlanes` — a lista GLOBAL do three —, e não pelo
// `OBC.Clipper` nem por `material.clippingPlanes`. Duas descobertas de 19/08/2026, no
// 522, que valem para qualquer mexida futura aqui:
//
//  1. NÃO mexer em `renderer.localClippingEnabled`. O @thatopen/components já o deixa
//     ligado; forçá-lo de novo faz o three percorrer `material.clippingPlanes` de todo
//     material da cena e o render morre em "toArray is not a function", tela branca.
//  2. O apoio visual do plano precisa ser MESH. Trocá-lo por LineSegments derrubou o
//     render inteiro (534 draw calls, framebuffer vazio) assim que um plano entrava.
//
// O preço do caminho global é que o apoio visual também é cortado — a lista global não
// tem exceção por objeto. Por isso ele é desenhado 1,5 cm para dentro do lado que fica.
type Eixo = "x" | "y" | "z";

function criarCorte(world: any, getBox: () => THREE.Box3) {
  const EIXO_IDX: Record<Eixo, 0 | 1 | 2> = { x: 0, y: 1, z: 2 };
  const estado: Record<Eixo, { on: boolean; t: number; dir: 1 | -1; v: number; plano: THREE.Plane; helper: THREE.Mesh }> =
    {} as any;

  const matHelper = new THREE.MeshBasicMaterial({
    color: 0x0d2543, transparent: true, opacity: 0.08,
    side: THREE.DoubleSide, depthWrite: false,
  });

  for (const e of ["x", "y", "z"] as Eixo[]) {
    const helper = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), matHelper);
    helper.visible = false;
    helper.renderOrder = 2;
    if (e === "y") helper.rotation.x = -Math.PI / 2;
    if (e === "x") helper.rotation.y = Math.PI / 2;
    world.scene.three.add(helper);
    estado[e] = { on: false, t: 0.5, dir: 1, v: 0, plano: new THREE.Plane(new THREE.Vector3(0, -1, 0), 0), helper };
  }

  const root = $("#lumos-corte");
  const btnLimpar = $("#corte-limpar") as HTMLButtonElement;
  const chkPav = $("#corte-por-pav") as HTMLInputElement;
  const chkPlano = $("#corte-mostrar-plano") as HTMLInputElement;
  const ctrl = (e: Eixo) => root.querySelector(`.corte-ctrl[data-for="${e}"]`) as HTMLElement;
  const slider = (e: Eixo) => ctrl(e).querySelector("input") as HTMLInputElement;

  function faixa(e: Eixo) {
    const box = getBox();
    const i = EIXO_IDX[e];
    if (box.isEmpty()) return { min: -50, max: 50 };
    const min = box.min.getComponent(i), max = box.max.getComponent(i);
    const folga = Math.max(0.5, (max - min) * 0.02);
    return { min: min - folga, max: max + folga };
  }
  function valor(e: Eixo) {
    const { min, max } = faixa(e);
    return min + (max - min) * estado[e].t;
  }

  function aplicar() {
    const ativos: THREE.Plane[] = [];
    const box = getBox();
    for (const e of ["x", "y", "z"] as Eixo[]) {
      const s = estado[e];
      const i = EIXO_IDX[e];
      const v = valor(e);
      s.v = v;
      // dir = 1 mantém o lado de baixo/antes do plano; -1 mantém o outro.
      const n = new THREE.Vector3(0, 0, 0);
      n.setComponent(i, -s.dir);
      s.plano.set(n, s.dir * v);
      if (s.on) ativos.push(s.plano);

      const mostra = s.on && chkPlano.checked && !box.isEmpty();
      s.helper.visible = mostra;
      if (mostra) {
        const size = box.getSize(new THREE.Vector3()).multiplyScalar(1.06);
        const c = box.getCenter(new THREE.Vector3());
        // PlaneGeometry(1,1) nasce no plano XY com normal +Z; cada rotação leva um
        // par de eixos do mundo para (largura, altura) local:
        //   x → rotation.y=+90°: largura = Z do mundo, altura = Y
        //   y → rotation.x=−90°: largura = X do mundo, altura = Z
        //   z → sem rotação:     largura = X do mundo, altura = Y
        const larg = e === "x" ? size.z : size.x;
        const alt = e === "y" ? size.z : size.y;
        s.helper.scale.set(Math.max(larg, 0.5), Math.max(alt, 0.5), 1);
        s.helper.position.copy(c);
        s.helper.position.setComponent(i, v - s.dir * 0.015);
      }

      ctrl(e).hidden = !s.on;
      const lado = e === "y"
        ? (s.dir === 1 ? "fica abaixo" : "fica acima")
        : (s.dir === 1 ? "fica antes" : "fica depois");
      (ctrl(e).querySelector(".val") as HTMLElement).textContent =
        `${v.toFixed(2).replace(".", ",")} m · ${lado}`;
      (root.querySelector(`.corte-eixos button[data-eixo="${e}"]`) as HTMLElement).classList.toggle("on", s.on);
    }
    const r = world.renderer?.three as THREE.WebGLRenderer | undefined;
    if (r) r.clippingPlanes = ativos;
    btnLimpar.hidden = ativos.length === 0;
    world.renderer?.update?.();
  }

  root.querySelectorAll<HTMLButtonElement>(".corte-eixos button").forEach(b => {
    b.addEventListener("click", () => {
      const e = b.dataset.eixo as Eixo;
      estado[e].on = !estado[e].on;
      if (estado[e].on) slider(e).value = String(Math.round(estado[e].t * 1000));
      aplicar();
    });
  });
  for (const e of ["x", "y", "z"] as Eixo[]) {
    slider(e).addEventListener("input", () => { estado[e].t = Number(slider(e).value) / 1000; aplicar(); });
    (ctrl(e).querySelector(".flip") as HTMLButtonElement).addEventListener("click", () => {
      estado[e].dir = estado[e].dir === 1 ? -1 : 1; aplicar();
    });
  }
  chkPlano.addEventListener("change", aplicar);
  btnLimpar.addEventListener("click", () => {
    for (const e of ["x", "y", "z"] as Eixo[]) estado[e].on = false;
    aplicar();
  });

  return {
    cortarNoPavimento: () => chkPav.checked,
    cortarEmY(cota: number) {
      const { min, max } = faixa("y");
      estado.y.on = true;
      estado.y.dir = 1;
      estado.y.t = Math.min(1, Math.max(0, (cota - min) / (max - min)));
      slider("y").value = String(Math.round(estado.y.t * 1000));
      aplicar();
    },
    // Quando o conjunto carregado muda, a faixa dos sliders passa a ser a extensão
    // real do modelo. Corte já posicionado mantém a COTA em metros — que é o que o
    // usuário enxerga —, não a fração do slider.
    recalibrar() {
      for (const e of ["x", "y", "z"] as Eixo[]) {
        const s = estado[e];
        if (!s.on) continue;
        const { min, max } = faixa(e);
        if (max > min) {
          s.t = Math.min(1, Math.max(0, (s.v - min) / (max - min)));
          slider(e).value = String(Math.round(s.t * 1000));
        }
      }
      aplicar();
    },
  };
}

function showLoader(msg: string, pct: number) {
  $("#lumos-loader").hidden = false;
  setLoaderProgress(msg, pct);
}
function setLoaderProgress(msg: string, pct: number) {
  const el = $("#lumos-loader");
  (el.querySelector(".bar > div") as HTMLElement).style.width = `${Math.max(5, pct)}%`;
  (el.querySelector("span") as HTMLElement).textContent = msg;
}
function hideLoader() { $("#lumos-loader").hidden = true; }

function showError(msg: string) {
  $("#lumos-error").hidden = false;
  $("#lumos-error-msg").textContent = msg;
}

function formatDate(iso: string) {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function downloadScreenshot(m: Manifest) {
  const canvas = document.querySelector("#lumos-canvas canvas") as HTMLCanvasElement | null;
  if (!canvas) return;
  const url = canvas.toDataURL("image/png");
  const a = document.createElement("a");
  a.href = url;
  a.download = `lumos-${m.projeto.numero}-${m.projeto.nome.replace(/\s+/g, "-")}.png`;
  a.click();
}
